/**
 * Extracts a user-friendly error message from API error responses.
 * Handles various error response formats:
 * - { error: { message: "..." } }
 * - { error: { code: "...", message: "..." } }
 * - { message: "..." }
 * - Plain string
 * - JSON string
 */
export function extractErrorMessage(error: unknown): string {
  // If it's already a string, try to parse it as JSON first
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      return extractErrorMessage(parsed);
    } catch {
      // Not JSON, return as-is if it's a reasonable length
      return error.length > 200 ? 'An unexpected error occurred' : error;
    }
  }

  // If it's an Error object, check if message is JSON
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message);
      return extractErrorMessage(parsed);
    } catch {
      // Message is not JSON, return it if reasonable
      return error.message.length > 200 ? 'An unexpected error occurred' : error.message;
    }
  }

  // If it's an object, try to extract the message
  if (error && typeof error === 'object') {
    const err = error as any;

    // Check for { error: { message: "..." } } format
    if (err.error?.message && typeof err.error.message === 'string') {
      return err.error.message;
    }

    // Check for { message: "..." } format
    if (err.message && typeof err.message === 'string') {
      return err.message;
    }

    // Check for { error: "..." } format
    if (err.error && typeof err.error === 'string') {
      return err.error;
    }

    // If we have an error object but no message, try to stringify it
    // but only if it's not too large
    try {
      const stringified = JSON.stringify(err);
      if (stringified.length > 200) {
        return 'An unexpected error occurred';
      }
      return stringified;
    } catch {
      return 'An unexpected error occurred';
    }
  }

  // Fallback
  return 'An unexpected error occurred';
}

